import { AxiosError } from 'axios';
import { del, edit, get, post, put } from '@/utils';
import { getValidToken } from '@/providers/auth/keycloak';
import { CITIZEN_API_URL } from '@/utils/axios/constants';
import {
	AdministrationLightDto,
	AssetDto,
	GeoJsonGeometry,
	GeometryCheckResultDto,
	MapBounds,
	PaginatedList,
	ProjectCreateDto,
	ProjectDto,
	ProjectListItemDto,
	ProjectLogDto,
	ProjectMapItemDto,
	ProjectStageDto,
	ProjectStatusDto,
	ProjectTypeDto,
	ProjectUpdateDto,
	ProjectsMapFilters,
	ProjectsQuery,
	PROJECT_STATUS_PUBLICAT
} from './types';

// ==============================|| PROIECTE (cityzen-be-v2) — API ACTIONS ||============================== //

// backend errors are RFC7807 ProblemDetails; validation errors carry an `errors` dictionary
export function apiErrorMessage(e: unknown, fallback: string): string {
	const data = (e as AxiosError)?.response?.data as
		| {
				detail?: string;
				title?: string;
				error?: string;
				errors?: Record<string, string[] | string>;
		  }
		| undefined;
	if (data) {
		if (data.errors && typeof data.errors === 'object') {
			const first = Object.values(data.errors).flat()[0];
			if (typeof first === 'string' && first) return first;
		}
		if (typeof data.detail === 'string' && data.detail) return data.detail;
		if (typeof data.title === 'string' && data.title) return data.title;
		if (typeof data.error === 'string' && data.error) return data.error;
	}
	return fallback;
}

export function toQueryString(params: Record<string, unknown>): string {
	const search = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value === undefined || value === null || value === '') return;
		search.append(key, String(value));
	});
	const qs = search.toString();
	return qs ? `?${qs}` : '';
}

export async function fetchProjects(
	query: ProjectsQuery = {}
): Promise<PaginatedList<ProjectListItemDto>> {
	try {
		const res = await get(`api/projects${toQueryString({ ...query })}`);
		return res.data as PaginatedList<ProjectListItemDto>;
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să accesăm lista de proiecte.'));
	}
}

export async function fetchProject(id: number): Promise<ProjectDto> {
	try {
		const res = await get(`api/projects/${id}`);
		return res.data as ProjectDto;
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să accesăm proiectul.'));
	}
}

export async function createProject(data: ProjectCreateDto): Promise<ProjectDto> {
	try {
		const res = await post<ProjectCreateDto, ProjectDto>('api/projects', data);
		return res.data as ProjectDto;
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să creăm proiectul.'));
	}
}

export async function updateProject(
	id: number,
	data: ProjectUpdateDto
): Promise<ProjectDto> {
	try {
		const res = await put<ProjectUpdateDto>(`api/projects/${id}`, data);
		return res.data as ProjectDto;
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să actualizăm proiectul.'));
	}
}

export async function deleteProject(id: number): Promise<void> {
	try {
		await del(`api/projects/${id}`);
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să ștergem proiectul.'));
	}
}

export async function publishProject(id: number): Promise<ProjectDto> {
	try {
		const res = await post(`api/projects/${id}/publish`);
		return res.data as ProjectDto;
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să publicăm proiectul.'));
	}
}

export async function archiveProject(id: number): Promise<ProjectDto> {
	try {
		const res = await post(`api/projects/${id}/archive`);
		return res.data as ProjectDto;
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să arhivăm proiectul.'));
	}
}

export async function checkGeometry(
	geometry: GeoJsonGeometry,
	administrationId?: number | null
): Promise<GeometryCheckResultDto> {
	try {
		const res = await post('api/projects/check-geometry', {
			geometry,
			administrationId: administrationId ?? undefined
		});
		return res.data as GeometryCheckResultDto;
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să verificăm geometria.'));
	}
}

export async function fetchProjectImages(
	id: number,
	pageSize = 100
): Promise<AssetDto[]> {
	try {
		const res = await get(
			`api/projects/${id}/images${toQueryString({ pageNumber: 1, pageSize })}`
		);
		return (res.data as PaginatedList<AssetDto>)?.items ?? [];
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să accesăm imaginile proiectului.'));
	}
}

export async function fetchProjectDocuments(
	id: number,
	pageSize = 100
): Promise<AssetDto[]> {
	try {
		const res = await get(
			`api/projects/${id}/documents${toQueryString({ pageNumber: 1, pageSize })}`
		);
		return (res.data as PaginatedList<AssetDto>)?.items ?? [];
	} catch (e) {
		throw new Error(
			apiErrorMessage(e, 'Nu am reușit să accesăm documentele proiectului.')
		);
	}
}

export async function fetchProjectLogs(
	id: number,
	pageNumber = 1,
	pageSize = 20
): Promise<PaginatedList<ProjectLogDto>> {
	try {
		const res = await get(
			`api/projects/${id}/logs${toQueryString({ pageNumber, pageSize })}`
		);
		return res.data as PaginatedList<ProjectLogDto>;
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să accesăm istoricul proiectului.'));
	}
}

export async function uploadAssets(files: File[]): Promise<AssetDto[]> {
	try {
		const form = new FormData();
		files.forEach((file) => form.append('files', file));
		const res = await post<FormData, AssetDto[]>('api/assets', form);
		return res.data as AssetDto[];
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să încărcăm fișierele.'));
	}
}

// ---- nomenclature lookups ----

export async function fetchProjectStages(): Promise<ProjectStageDto[]> {
	try {
		const res = await get(`api/project-stages${toQueryString({ pageSize: 100 })}`);
		const list = (res.data as PaginatedList<ProjectStageDto>)?.items ?? [];
		return [...list].sort((a, b) => a.position - b.position);
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să accesăm stagiile de proiect.'));
	}
}

export async function fetchProjectStatuses(): Promise<ProjectStatusDto[]> {
	try {
		const res = await get(`api/project-statuses${toQueryString({ pageSize: 100 })}`);
		return (res.data as PaginatedList<ProjectStatusDto>)?.items ?? [];
	} catch (e) {
		throw new Error(
			apiErrorMessage(e, 'Nu am reușit să accesăm statusurile de proiect.')
		);
	}
}

export async function fetchProjectTypes(search?: string): Promise<ProjectTypeDto[]> {
	try {
		const res = await get(`api/project-types${toQueryString({ pageSize: 200, search })}`);
		return (res.data as PaginatedList<ProjectTypeDto>)?.items ?? [];
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să accesăm tipurile de proiect.'));
	}
}

export async function createProjectType(
	name: string,
	administrationId?: number | null
): Promise<ProjectTypeDto> {
	try {
		const res = await post('api/project-types', {
			name,
			administrationId: administrationId ?? undefined
		});
		return res.data as ProjectTypeDto;
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să creăm tipul de proiect.'));
	}
}

export async function fetchAdministrationsLight(): Promise<AdministrationLightDto[]> {
	try {
		const res = await get('api/administrations/light');
		return (res.data as AdministrationLightDto[]) ?? [];
	} catch {
		// permission-gated (administrations:read) — callers treat an empty list as "not available"
		return [];
	}
}

// ---- composite helpers ----

// stagiul are endpoint dedicat (PATCH) — permis și pe proiecte publicate,
// spre deosebire de PUT-ul complet care e doar pentru ciorne
export async function changeProjectStage(
	id: number,
	stageId: number
): Promise<ProjectDto> {
	try {
		const res = await edit(`api/projects/${id}/stage`, { stageId });
		return res.data as ProjectDto;
	} catch (e) {
		throw new Error(apiErrorMessage(e, 'Nu am reușit să actualizăm stagiul.'));
	}
}

// "Duplică" — copies geometry, images and phases into a new project.
// options.title overrides the default "<title> (copie)"; options.asDraft === false
// re-publishes the copy when the source was published.
export async function duplicateProject(
	id: number,
	options?: { title?: string; asDraft?: boolean }
): Promise<ProjectDto> {
	const [detail, images, documents] = await Promise.all([
		fetchProject(id),
		fetchProjectImages(id),
		fetchProjectDocuments(id)
	]);
	const created = await createProject({
		title: options?.title?.trim() || `${detail.title} (copie)`,
		description: detail.description,
		administrationId: detail.administration?.id,
		stageId: detail.stage.id,
		typeId: detail.type.id,
		startDate: detail.startDate,
		endDate: detail.endDate,
		geometries: detail.geometries,
		phases: detail.phases.map((p) => ({
			title: p.title,
			description: p.description,
			startDate: p.startDate,
			endDate: p.endDate
		})),
		images: images.map((a) => a.id),
		documents: documents.map((a) => a.id)
	});
	if (options?.asDraft === false && detail.status.id === PROJECT_STATUS_PUBLICAT) {
		return publishProject(created.id);
	}
	return created;
}

// ---- poster (PDF download) ----

export async function downloadProjectPoster(id: number, title: string): Promise<void> {
	const token = await getValidToken();
	const base = CITIZEN_API_URL.replace(/\/$/, '');
	const res = await fetch(`${base}/api/projects/${id}/poster`, {
		headers: token ? { Authorization: `Bearer ${token}` } : undefined
	});
	if (!res.ok) throw new Error('Nu am reușit să generăm posterul.');
	const blob = await res.blob();
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = `${title || 'proiect'}-poster.pdf`;
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
}

// ---- map stream (NDJSON) ----

export async function streamProjectsMap(
	bounds: MapBounds,
	filters: ProjectsMapFilters,
	onItem: (item: ProjectMapItemDto) => void,
	signal?: AbortSignal
): Promise<void> {
	const token = await getValidToken();
	const base = CITIZEN_API_URL.replace(/\/$/, '');
	const qs = toQueryString({
		north: bounds.north,
		south: bounds.south,
		east: bounds.east,
		west: bounds.west,
		zoom: bounds.zoom,
		typeId: filters.typeId,
		stageId: filters.stageId,
		statusId: filters.statusId
	});
	const res = await fetch(`${base}/api/projects/map${qs}`, {
		headers: {
			Accept: 'application/x-ndjson',
			...(token ? { Authorization: `Bearer ${token}` } : {})
		},
		signal
	});
	if (!res.ok || !res.body) {
		throw new Error('Nu am reușit să încărcăm proiectele pe hartă.');
	}
	const reader = res.body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';
	for (;;) {
		// eslint-disable-next-line no-await-in-loop -- NDJSON chunks must be read sequentially
		const { done, value } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });
		const lines = buffer.split('\n');
		buffer = lines.pop() ?? '';
		for (const line of lines) {
			const trimmed = line.trim();
			if (trimmed) onItem(JSON.parse(trimmed) as ProjectMapItemDto);
		}
	}
	const rest = buffer.trim();
	if (rest) onItem(JSON.parse(rest) as ProjectMapItemDto);
}
